import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container, CardActionArea, Card, Typography, Box, Avatar, CircularProgress, useMediaQuery, useTheme,
  Dialog, DialogContent, DialogTitle, DialogContentText, DialogActions, IconButton, TextField, List, ListItem, Button,
  Slide, Tabs, Tab
} from '@mui/material';
import { Masonry } from '@mui/lab';
import { useAuth } from '../context/AuthProvider.tsx';
import { fetchCapsule } from '../api/GetCapsules.ts';
import GetCapsuleImages from '../api/GetCapsuleImages.ts';
import ToggleFavourite from '../api/ToggleFavourite.ts';
import { ChevronLeft, ChevronRight, LockOutlined, LockOpenOutlined, Image as ImageIcon, Close, Send, ChatBubbleOutline, FavoriteBorderOutlined, Favorite, Download, Delete } from '@mui/icons-material';
import AddComment from '../api/AddComment.ts';
import DeleteComment from '../api/DeleteComment.ts';
import { motion } from 'framer-motion';
import { ThemeType, themeConfigs } from '../components/ThemeConfigs.ts';
import { useSwipeable } from 'react-swipeable';

interface Capsule {
  capsuleID: number;
  title: string;
  description: string;
  message: string;
  images: {
    image: string;
    favourites?: string[];
    comments: {
      user: string;
      comment: string;
      timestamp: string;
    }[];
    owner: string;
    timestamp: string;
  }[];
  unlock: string;
  owner: string;
  access: string[];
  theme: string;
}

const CapsulePage = () => {
  const { id } = useParams<{ id: string }>();
  const [capsules, setCapsules] = useState<Capsule[]>([]);
  const [loading, setLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(true);
  const { user } = useAuth();
  const [capsuleImages, setCapsuleImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [newComment, setNewComment] = useState('');
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [view, setView] = useState<'all' | 'favorites'>('all');
  const [localFavorites, setLocalFavorites] = useState<{ [key: number]: boolean }>({});
  const [deleteCommentDialogOpen, setDeleteCommentDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<{ index: number; timestamp: string } | null>(null);
  const [showLockOpen, setShowLockOpen] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);


  const handleOpenMessageDialog = useCallback(() => {
    setMessageDialogOpen(true);
  }, []);

  const handleCloseMessageDialog = useCallback(() => {
    setMessageDialogOpen(false);
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const unlockTimer = setTimeout(() => {
      setShowLockOpen(true);
      setTimeout(() => {
        setIsUnlocked(true);
      }, 1000);
    }, 1500);

    return () => clearTimeout(unlockTimer);
  }, []);

  useEffect(() => {
    const fetchCapsuleData = async () => {
      if (!user) return;
      setLoading(true);
      setImagesLoading(true);
      try {
        const fetchedCapsules = await fetchCapsule(user?.username, id!);
        setCapsules(fetchedCapsules);
        if (fetchedCapsules.length > 0) {
          const capsule = fetchedCapsules.find(c => c.capsuleID === parseInt(id || ''));
          if (capsule) {
            const initialFavorites = capsule.images.reduce((acc, img, index) => {
              acc[index] = img.favourites?.includes(user.username) || false;
              return acc;
            }, {} as { [key: number]: boolean });
            setLocalFavorites(initialFavorites);

            // Fetch images and add a minimum delay
            const fetchImages = async () => {
              await GetCapsuleImages(capsule.capsuleID.toString(), setCapsuleImages);
              // Add a minimum delay of 1 second for smooth transition
              await new Promise(resolve => setTimeout(resolve, 1000));
              setImagesLoading(false);
            };
            fetchImages();
          }
        }
      } catch (error) {
        console.error('Error fetching capsules:', error);
        setImagesLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchCapsuleData();
  }, [user, id]);

  useEffect(() => {
    const unlockTimer = setTimeout(() => {
      setShowLockOpen(true);
      setTimeout(() => {
        setIsUnlocked(true);
      }, 800);
    }, 500);

    return () => clearTimeout(unlockTimer);
  }, []);

  const capsule = useMemo(() => capsules.find(capsule => capsule.capsuleID === parseInt(id || '')), [capsules, id]);
  const capsuleTheme = capsule?.theme as ThemeType || 'classic';
  const themeConfig = themeConfigs[capsuleTheme];

  const handleImageClick = useCallback((index: number) => {
    setSelectedImageIndex(index);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedImageIndex(null);
    setNewComment('');
    setCommentsOpen(false);
  }, []);

  const handlePrevImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev === null || prev === 0 ? capsuleImages.length - 1 : prev - 1));
  }, [capsuleImages.length]);

  const handleNextImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev === null || prev === capsuleImages.length - 1 ? 0 : prev + 1));
  }, [capsuleImages.length]);

  const handleDownloadImage = (imageUrl: string) => {
    window.open(imageUrl, "_blank");
  };

  const handleAddComment = useCallback(async (commentText: string) => {
    if (commentText.trim() && capsule && selectedImageIndex !== null) {
      const userName = user?.username || 'Anonymous';
      const timestamp = new Date().toISOString();

      try {
        setCapsules(prevCapsules => {
          const updatedCapsules = [...prevCapsules];
          const capsuleIndex = updatedCapsules.findIndex(c => c.capsuleID === capsule.capsuleID);
          if (capsuleIndex !== -1) {
            const updatedCapsule = { ...updatedCapsules[capsuleIndex] };
            updatedCapsule.images[selectedImageIndex].comments.push({
              user: userName,
              comment: commentText,
              timestamp: timestamp
            });
            updatedCapsules[capsuleIndex] = updatedCapsule;
          }
          return updatedCapsules;
        });
      } catch (error) {
        console.error('Error adding comment:', error);
      }

      const response = await AddComment(capsule.capsuleID.toString(), userName, selectedImageIndex.toString(), commentText, timestamp);

      if (!response) {
        console.error('Comment was not added to the server');
        setCapsules(prevCapsules => {
          const updatedCapsules = [...prevCapsules];
          const capsuleIndex = updatedCapsules.findIndex(c => c.capsuleID === capsule.capsuleID);
          if (capsuleIndex !== -1) {
            const updatedCapsule = { ...updatedCapsules[capsuleIndex] };
            updatedCapsule.images[selectedImageIndex].comments.pop();
            updatedCapsules[capsuleIndex] = updatedCapsule;
          }
          return updatedCapsules;
        });
      }
    }
  }, [capsule, selectedImageIndex, user]);

  const toggleComments = useCallback(() => {
    setCommentsOpen(prev => !prev);
  }, []);

  const openComments = useCallback(() => {
    setCommentsOpen(true);
  }, []);

  const closeComments = useCallback(() => {
    setCommentsOpen(false);
  }, []);

  const handlers = useSwipeable({
    onSwipedLeft: handleNextImage,
    onSwipedRight: handlePrevImage,
    onSwipedUp: openComments,
    onSwipedDown: (eventData) => {
      if (commentsOpen) {
        closeComments();
      } else {
        handleCloseDialog();
      }
    },
    trackMouse: true
  });

  const tabHandlers = useSwipeable({
    onSwipedLeft: () => setView('favorites'),
    onSwipedRight: () => setView('all'),
    trackMouse: true
  });

  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNewComment(e.target.value);
  }, []);

  const toggleFavorite = useCallback(async (imageIndex: number) => {
    if (!user || !capsule) return;

    setLocalFavorites(prev => ({ ...prev, [imageIndex]: !prev[imageIndex] }));

    try {
      await ToggleFavourite(capsule.capsuleID.toString(), imageIndex.toString(), user.username);

      setCapsules(prevCapsules => {
        const updatedCapsules = [...prevCapsules];
        const capsuleIndex = updatedCapsules.findIndex(c => c.capsuleID === capsule.capsuleID);
        if (capsuleIndex !== -1) {
          const updatedCapsule = { ...updatedCapsules[capsuleIndex] };
          if (!updatedCapsule.images[imageIndex].favourites) {
            updatedCapsule.images[imageIndex].favourites = [];
          }
          const favorites = updatedCapsule.images[imageIndex].favourites;
          const userIndex = favorites.indexOf(user.username);
          if (userIndex === -1) {
            favorites.push(user.username);
          } else {
            favorites.splice(userIndex, 1);
          }
          updatedCapsules[capsuleIndex] = updatedCapsule;
        }
        return updatedCapsules;
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setLocalFavorites(prev => ({ ...prev, [imageIndex]: !prev[imageIndex] }));
    }
  }, [capsule, user]);

  const isFavorite = useCallback((imageIndex: number) => {
    return localFavorites[imageIndex] || false;
  }, [localFavorites]);

  const handleDeleteComment = useCallback(async (commentIndex: number) => {
    if (selectedImageIndex === null || !capsule) return;
    const comment = capsule.images[selectedImageIndex].comments[commentIndex];
    setCommentToDelete({ index: commentIndex, timestamp: comment.timestamp });
    setDeleteCommentDialogOpen(true);
  }, [capsule, selectedImageIndex]);

  const confirmDeleteComment = useCallback(async () => {
    if (selectedImageIndex === null || !capsule || !commentToDelete) return;

    setDeleteCommentDialogOpen(false);

    setCapsules(prevCapsules => {
      const updatedCapsules = [...prevCapsules];
      const capsuleIndex = updatedCapsules.findIndex(c => c.capsuleID === capsule.capsuleID);
      if (capsuleIndex !== -1) {
        const updatedCapsule = { ...updatedCapsules[capsuleIndex] };
        updatedCapsule.images[selectedImageIndex].comments.splice(commentToDelete.index, 1);
        updatedCapsules[capsuleIndex] = updatedCapsule;
      }
      return updatedCapsules;
    });

    try {
      await DeleteComment(capsule.capsuleID, selectedImageIndex.toString(), commentToDelete.timestamp);
    } catch (error) {
      console.error('Error deleting comment:', error);
      setCapsules(prevCapsules => {
        const updatedCapsules = [...prevCapsules];
        const capsuleIndex = updatedCapsules.findIndex(c => c.capsuleID === capsule.capsuleID);
        if (capsuleIndex !== -1) {
          const updatedCapsule = { ...updatedCapsules[capsuleIndex] };
          updatedCapsule.images[selectedImageIndex].comments = capsule.images[selectedImageIndex].comments;
          updatedCapsules[capsuleIndex] = updatedCapsule;
        }
        return updatedCapsules;
      });
    }
    setCommentToDelete(null);
  }, [capsule, selectedImageIndex, commentToDelete]);

  const CommentsSection = () => {
    const [localComment, setLocalComment] = useState('');

    const handleLocalCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalComment(e.target.value);
    };

    const handleAddLocalComment = async () => {
      if (localComment.trim()) {
        await handleAddComment(localComment);
        setLocalComment('');
      }
    };

    if (selectedImageIndex === null || !capsule || !capsule.images[selectedImageIndex]) {
      return null;
    }

    return (
      <Box sx={{
        width: '100%',
        height: '100%',
        bgcolor: '#1E1E1E',
        color: "white",
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: `1px solid rgba(255,255,255,0.1)`,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "white" }}>Comments</Typography>
          {isMobile && (
            <IconButton onClick={() => setCommentsOpen(false)} size="small" sx={{ color: "white" }}>
              <Close />
            </IconButton>
          )}
        </Box>
        <List sx={{
          flex: 1,
          overflowY: 'auto',
          p: 0,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#121212',
          },
          '&::-webkit-scrollbar-thumb': {
            background: themeConfig.accentColor,
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: themeConfig.buttonColor,
          },
        }}>
          {capsule.images[selectedImageIndex].comments.map((comment, index) => (
            <ListItem key={index} alignItems="flex-start" sx={{ py: 2, px: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: themeConfig.accentColor }}>
                    @{comment.user}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#B0B0B0', ml: 2 }}>
                    {new Date(comment.timestamp).toLocaleString()}
                  </Typography>
                  {comment.user === user?.username && (
                    <IconButton onClick={() => handleDeleteComment(index)} size="small" sx={{ color: 'red', ml: 'auto' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: 'white', lineHeight: 1.6 }}>
                  {comment.comment}
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>
        <Box sx={{
          p: 2,
          borderTop: `1px solid rgba(255,255,255,0.1)`,
          display: 'flex',
          alignItems: 'center',
        }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Add a comment..."
            value={localComment}
            onChange={handleLocalCommentChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddLocalComment();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.1)',
                },
                '&:hover fieldset': {
                  borderColor: themeConfig.accentColor,
                },
                '&.Mui-focused fieldset': {
                  borderColor: themeConfig.accentColor,
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#B0B0B0',
                opacity: 1,
              },
            }}
          />
          <IconButton onClick={handleAddLocalComment} sx={{ ml: 1, color: themeConfig.accentColor }}>
            <Send />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const toggleFilterMenu = () => {
    setFilterMenuOpen(prev => !prev);
  };

  const handleViewChange = (newView: 'all' | 'favorites') => {
    setView(newView);
    setFilterMenuOpen(false);
  };

  const displayedImages = useMemo(() => {
    if (!capsule) return [];
    return capsuleImages
      .map((imageUrl, originalIndex) => ({ imageUrl, originalIndex }))
      .filter(({ originalIndex }) => view === 'all' || isFavorite(originalIndex));
  }, [capsuleImages, view, isFavorite, capsule]);

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!capsule) {   
    return (
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: '#0A0F15',
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3
      }}>
        <Box 
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          sx={{ 
            textAlign: 'center',
            maxWidth: 500,
            mb: 4
          }}
        >
          <Box sx={{ mb: 3 }}>
            <ImageIcon sx={{ fontSize: 80, mb: 2 }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
            Capsule Not Found
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 18, mb: 4 }}>
            The time capsule you're looking for might have been deleted or doesn't exist.
          </Typography>
          <Button 
            component={Link} 
            to="/"
            variant="contained"
            startIcon={<ChevronLeft />}
            sx={{
              background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Box>
    );
  }



  // Add a new function for the ambient animation
  const AmbientBackground = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        overflow: 'hidden',
      }}
    >
      <motion.div
        initial={{ opacity: 0.5, scale: 1 }}
        animate={{
          opacity: [0.5, 0.8, 0.5],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          right: '-50%',
          bottom: '-50%',
          background: `radial-gradient(circle, ${themeConfig.accentColor}33 0%, transparent 70%)`,
        }}
      />
    </Box>
  );


  return (
    <>
      {!isUnlocked ? (
        <motion.div
          key="lock"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            position: isMobile ? 'fixed' : 'relative',
            top: isMobile ? 0 : '50%',
            left: 0,
            right: 0,
            bottom: isMobile ? 80 : 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <motion.div
            initial={{ x: -50 }}
            animate={showLockOpen ? { x: 50, rotateY: 180 } : {}}
            transition={{ duration: 0.5 }}
            style={{ transformStyle: 'preserve-3d' }}
          >
            <motion.div
              style={{
                backfaceVisibility: 'hidden',
                position: 'absolute',
              }}
              animate={showLockOpen ? { opacity: 0 } : { opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <LockOutlined sx={{ fontSize: 100, color: themeConfig.accentColor, stroke: "#0A0F15", strokeWidth: 1 }} />
            </motion.div>
            <motion.div
              style={{
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                position: 'absolute',
              }}
              animate={showLockOpen ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <LockOpenOutlined sx={{ fontSize: 100, color: themeConfig.accentColor, stroke: "#0A0F15", strokeWidth: 1 }} />
            </motion.div>
          </motion.div>
        </motion.div>
      ) : (
        <Box sx={{
          minHeight: '100vh',
          backgroundColor: '#0A0F15',
          color: '#FFFFFF',
          position: 'relative',
          overflow: 'hidden',
        }} {...tabHandlers}>
          <AmbientBackground />
          {/* Header */}
          <Box sx={{ p: 2, pb: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, pt: 2 }}>
              <IconButton
                onClick={() => window.history.back()}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                  zIndex: 2
                }}
              >
                <ChevronLeft />
              </IconButton>
              <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                overflow: 'hidden',
                border: `2px solid ${themeConfig.accentColor}`
              }}>
                <img
                  src={`https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/${capsule?.owner}/profilepic.jpeg`}
                  alt="Profile"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Box>

            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, mb: 1 }}>
              Unlocked {(new Date(capsule.unlock).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }))}
            </Typography>

            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              {capsule.title}
            </Typography>

            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, mb: 3 }}>
              {capsule.images.length} Photos • {capsule.access.length} Participants
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              {capsule.message && (
                <Button
                  onClick={handleOpenMessageDialog}
                  startIcon={<ChatBubbleOutline />}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 'full',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                  }}
                >
                  Secret Message
                </Button>
              )}
            </Box>

            {/* Tabs */}
            <Tabs
              value={view}
              onChange={(_, newValue) => setView(newValue)}
              sx={{
                ml: 1,
                '& .MuiTabs-indicator': {
                  backgroundColor: themeConfig.accentColor,
                },
                '& .MuiTab-root': {
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-selected': {
                    color: themeConfig.accentColor,
                  },
                },
              }}
            >
              <Tab label="Grid" value="all" />
              <Tab label="Favourites" value="favorites" />
            </Tabs>


            {/* Enhanced Media Grid */}
            {imagesLoading ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                py: 8,
                px: 2,
                textAlign: 'center',
                minHeight: '400px',
                width: '100%',
                position: 'relative',
                left: '50%',
                transform: 'translateX(-50%)'
              }}>
                <CircularProgress sx={{ color: themeConfig.accentColor, mb: 2 }} />
              
              </Box>
            ) : capsuleImages.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                py: 8,
                px: 2,
                textAlign: 'center',
                minHeight: '400px',
                width: '100%',
                position: 'relative',
                left: '50%',
                transform: 'translateX(-50%)'
              }}>
                <ImageIcon sx={{ fontSize: 48, color: themeConfig.accentColor, mb: 2 }} />
                <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                  No Memories Yet
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: '400px' }}>
                  This time capsule is empty. Add some photos or videos to capture your memories.
                </Typography>
              </Box>
            ) : (
              <Masonry columns={{ xs: 2, sm: 2, md: 4 }} spacing={isMobile ? 0.5 : 1} sx={{ mb: 4 }}>
                {displayedImages.length === 0 ? (
                  <Box sx={{ 
                    gridColumn: '1 / -1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    px: 2,
                    textAlign: 'center',
                    minHeight: '400px',
                    width: '100%',
                    position: 'relative',
                    left: '50%',
                    transform: 'translateX(-50%)'
                  }}>
                    <FavoriteBorderOutlined sx={{ fontSize: 48, color: themeConfig.accentColor, mb: 2 }} />
                    <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                      No Favourited Images Yet
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: '400px' }}>
                      Start favoriting your favorite memories by clicking the heart icon on any image.
                    </Typography>
                  </Box>
                ) : (
                  displayedImages.map(({ imageUrl, originalIndex }) => (
                    <Card key={originalIndex} sx={{
                      position: 'relative',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease',
                      backgroundColor: themeConfig.cardBackground,
                      boxShadow: themeConfig.cardShadow,
                    }}>
                      <CardActionArea onClick={() => handleImageClick(originalIndex)}>
                        {imageUrl.includes('video') ? (
                          <video
                            controls
                            src={`${imageUrl}#t=0.1`}
                            style={{
                              width: '100%',
                              height: 250,
                              objectFit: 'cover',
                              display: 'block'
                            }}
                          />
                        ) : (
                          <img
                            src={imageUrl || "/placeholder.svg"}
                            alt={`Memory ${originalIndex + 1}`}
                            style={{
                              width: '100%',
                              height: 250,
                              objectFit: 'cover',
                              display: 'block'
                            }}
                          />
                        )}

                        <Box sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          p: 1,
                          background: themeConfig.cardOverlayGradient,
                          color: themeConfig.cardOverlayTextColor
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(originalIndex);
                              }}
                              sx={{ color: isFavorite(originalIndex) ? themeConfig.favoriteColor : "white" }}
                            >
                              {isFavorite(originalIndex) ? <Favorite /> : <FavoriteBorderOutlined />}
                            </IconButton>
                            <Typography variant="body2">
                              {capsule.images[originalIndex]?.comments.length || 0} <ChatBubbleOutline />
                            </Typography>
                          </Box>
                        </Box>
                      </CardActionArea>
                    </Card>
                  ))
                )}
              </Masonry>
            )}

            <Dialog
              open={selectedImageIndex !== null}
              onClose={handleCloseDialog}
              maxWidth={false}
              fullScreen={isMobile}
              PaperProps={{
                sx: {
                  bgcolor: 'background.paper',
                  width: isMobile ? '100%' : '90vw',
                  height: isMobile ? '100%' : '90vh',
                  maxWidth: 'none',
                  maxHeight: 'none',
                }
              }}
            >
              <DialogContent sx={{
                p: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                ...(isMobile && {
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '60%',
                    zIndex: 0,
                  },
                }),
              }} {...handlers}>
                {isMobile && (
                  <Slide direction="up" in={commentsOpen} mountOnEnter unmountOnExit>
                    <Box sx={{
                      position: 'fixed',
                      borderTop: 'solid',
                      borderColor: themeConfig.dividerColor,
                      borderWidth: 1,
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '60%',
                      bgcolor: themeConfig.cardBackground,
                      zIndex: 1,
                      overflowY: 'auto',
                      borderTopLeftRadius: 16,
                      borderTopRightRadius: 16,
                      transform: commentsOpen ? 'translateY(0)' : 'translateY(100%)',
                      '&::-webkit-scrollbar': {
                        display: 'none',
                      },
                      msOverflowStyle: 'none',
                      scrollbarWidth: 'none',
                    }}>
                      <CommentsSection />
                    </Box>
                  </Slide>
                )}
                <Box sx={{ position: 'relative', flex: 1, bgcolor: 'black', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <IconButton
                    onClick={handleCloseDialog}
                    sx={{ position: 'absolute', right: 8, top: 8, color: 'white', zIndex: 1 }}
                  >
                    <Close />
                  </IconButton>
                  {!commentsOpen && (
                    <>
                      <IconButton
                        onClick={handlePrevImage}
                        sx={{
                          position: 'absolute',
                          left: 14,
                          top: '50%',
                          color: 'white',
                          zIndex: 1,
                          bgcolor: 'rgba(0, 0, 0, 0.5)',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                          },
                        }}
                      >
                        <ChevronLeft />
                      </IconButton>
                      <IconButton
                        onClick={handleNextImage}
                        sx={{
                          position: 'absolute',
                          right: 14,
                          top: '50%',
                          color: 'white',
                          zIndex: 1,
                          bgcolor: 'rgba(0, 0, 0, 0.5)',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                          },
                        }}
                      >
                        <ChevronRight />
                      </IconButton>
                    </>
                  )}
                  {selectedImageIndex !== null && (
                    capsuleImages[selectedImageIndex].includes('video') ? (
                      <video
                        controls
                        preload="metadata"
                        src={`${capsuleImages[selectedImageIndex]}#t=0.1`}
                        style={{ borderRadius: '8px', width: isMobile ? '95%' : 'auto', maxWidth: '95%', maxHeight: isMobile ? '80vh' : '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <img
                        src={capsuleImages[selectedImageIndex] || "/placeholder.svg"}
                        alt={`Capsule Image ${selectedImageIndex + 1}`}
                        style={{ borderRadius: '8px', width: isMobile ? '95%' : 'auto', maxWidth: '95%', maxHeight: isMobile ? '80vh' : '100%', objectFit: 'cover' }}
                      />
                    )
                  )}
                  {selectedImageIndex !== null && (
                    <Box sx={{ position: 'fixed', left: 8, bottom: 8, display: 'flex', gap: 2 }}>
                      <IconButton
                        onClick={() => toggleFavorite(selectedImageIndex)}
                        sx={{ color: isFavorite(selectedImageIndex) ? themeConfig.favoriteColor : 'white' }}
                      >
                        {isFavorite(selectedImageIndex) ? <Favorite /> : <FavoriteBorderOutlined />}
                      </IconButton>
                      {isMobile && (
                        <IconButton
                          onClick={toggleComments}
                          sx={{ color: 'white' }}
                        >
                          <ChatBubbleOutline />
                        </IconButton>
                      )}
                      {!isMobile && (
                        <IconButton
                          onClick={() => handleDownloadImage(capsuleImages[selectedImageIndex])}
                          sx={{ color: 'white' }}
                        >
                          <Download />
                        </IconButton>
                      )}
                    </Box>
                  )}
                  {selectedImageIndex !== null && capsule.images[selectedImageIndex] && (
                    <Box sx={{ position: 'absolute', left: 20, top: 12, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={`https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/${capsule.images[selectedImageIndex].owner}/profilepic.jpeg`}
                        alt={capsule.images[selectedImageIndex].owner}
                      />
                      <Typography variant="caption" sx={{ color: 'yellow' }}>
                        {new Date(capsule.images[selectedImageIndex].timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {!isMobile && selectedImageIndex !== null &&
                  <Box sx={{ width: '400px', height: '100%', overflowY: 'auto' }}>
                    <CommentsSection />
                  </Box>
                }
              </DialogContent>
            </Dialog>

            <Dialog
              open={deleteCommentDialogOpen}
              onClose={() => setDeleteCommentDialogOpen(false)}
              PaperProps={{
                style: {
                  backgroundColor: 'white',
                  color: 'black',
                },
              }}
            >
              <DialogTitle>Confirm Deletion</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ color: 'rgba(0,0,0,0.5)' }}>
                  Are you sure you want to delete this comment? This action cannot be undone.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteCommentDialogOpen(false)} sx={{ color: 'black' }}>
                  Cancel
                </Button>
                <Button onClick={confirmDeleteComment} sx={{ color: 'error.main' }} autoFocus>
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog
              open={messageDialogOpen}
              onClose={handleCloseMessageDialog}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                elevation: 0,
                sx: {
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0))',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              <DialogContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <ChatBubbleOutline sx={{ fontSize: 32, color: themeConfig.accentColor, mr: 2 }} />
                  <Typography variant="h5" sx={{ 
                    color: themeConfig.accentColor,
                    fontWeight: 600,
                    background: `linear-gradient(45deg, ${themeConfig.accentColor}, ${themeConfig.buttonColor})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}>
                    Secret Message
                  </Typography>
                </Box>
                <Typography sx={{ 
                  color: "text.primary",
                  fontFamily: "'Roboto Slab', serif",
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  mb: 3
                }}>
                  {capsule.message}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={handleCloseMessageDialog}
                    sx={{
                      color: themeConfig.accentColor,
                      '&:hover': {
                        backgroundColor: `${themeConfig.accentColor}10`,
                      },
                    }}
                  >
                    Close
                  </Button>
                </Box>
              </DialogContent>
            </Dialog>
          </Box>
        </Box>
      )}
    </>
  );
};

export default CapsulePage;
